"use client";

import * as React from "react";

import type { StaticImageData } from "next/image";
import Image from "next/image";

import { cn } from "@repo/ui/lib/utils";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/accordion";

export interface AccordionFieldItem {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  // Icon can be a React node (e.g., lucide icon) or an image source (SVG/PNG) via StaticImageData/string
  icon?: React.ReactNode | StaticImageData | string;
  iconAlt?: string;
  iconSize?: number; // defaults to 20
  // Optional alternative icon when the item is open (dynamic icon swap)
  iconOpen?: React.ReactNode | StaticImageData | string;
  iconOpenAlt?: string;
  iconOpenSize?: number;
  rightText?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  // Optional per-item style overrides
  triggerClassName?: string;
  titleClassName?: string;
  rightTextClassName?: string;
  contentClassName?: string;
}

type CommonProps = {
  items: AccordionFieldItem[];
  className?: string;
  variant?: "default" | "bordered" | "minimal";
  size?: "sm" | "md" | "lg";
  // Optional global style overrides for dynamic templates/colors
  containerClassName?: string;
  itemClassName?: string;
  triggerClassName?: string;
  titleClassName?: string;
  rightTextClassName?: string;
  contentClassName?: string;
  // New optional styling hooks for dynamic theming
  itemBorderClassName?: string; // applied to AccordionItem wrapper
  triggerBgClassName?: string; // background classes for trigger
  triggerTextOpenClassName?: string; // applied only when open: e.g. "text-brand-cyan"
  contentBgClassName?: string; // background for content container
  // Trigger icon controls (maps to ui/accordion props)
  triggerIcon?: React.ReactNode;
  triggerIconPosition?: "left" | "right";
  // Default icon rendering options for items when using image sources
  defaultIconAlt?: string;
  defaultIconSize?: number; // defaults to 20
};

type SingleAccordionProps = {
  type?: "single"; // default
  collapsible?: boolean;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

type MultipleAccordionProps = {
  type: "multiple";
  // Radix ignores collapsible for multiple; disallow to avoid type errors
  collapsible?: never;
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
};

export type AccordionFieldProps = CommonProps & (SingleAccordionProps | MultipleAccordionProps);

const AccordionField = React.forwardRef<React.ElementRef<typeof Accordion>, AccordionFieldProps>(
  (props, ref) => {
    const {
      items,
      className,
      variant = "default",
      size = "md",
      containerClassName,
      itemClassName,
      triggerClassName,
      titleClassName,
      rightTextClassName,
      contentClassName,
    } = props;
    const variantStyles = {
      default: "border rounded-lg bg-white",
      bordered: "border-2 border-gray-200 rounded-lg bg-white",
      minimal: "bg-transparent",
    };

    const sizeStyles = {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    };

    const containerClass = cn(variantStyles[variant], sizeStyles[size], className);

    // Helper to render either ReactNode icons or image sources dynamically
    const renderItemIcon = (
      icon: AccordionFieldItem["icon"],
      alt?: string,
      size?: number,
    ): React.ReactNode => {
      if (!icon) return null;
      const finalSize = size ?? props.defaultIconSize ?? 20;
      // If it's already a valid React element (e.g., lucide icon), render as-is
      if (React.isValidElement(icon)) {
        return <span className="text-inherit">{icon}</span>;
      }
      // String path
      if (typeof icon === "string") {
        return (
          <Image
            src={icon}
            alt={alt ?? props.defaultIconAlt ?? "icon"}
            width={finalSize}
            height={finalSize}
            className="h-5 w-5"
          />
        );
      }
      // StaticImageData-like object (from import): has a 'src' string
      if (typeof icon === "object" && icon && "src" in icon && typeof icon.src === "string") {
        return (
          <Image
            src={(icon as StaticImageData).src}
            alt={alt ?? props.defaultIconAlt ?? "icon"}
            width={finalSize}
            height={finalSize}
            className="h-5 w-5"
          />
        );
      }
      // Fallback to rendering as node
      return <span className="text-inherit">{icon as React.ReactNode}</span>;
    };

    // Render closed and open icons with Radix data-state classes when both provided
    const renderIcons = (item: AccordionFieldItem) => {
      const closed = renderItemIcon(item.icon, item.iconAlt, item.iconSize);
      const opened = renderItemIcon(
        item.iconOpen ?? null,
        item.iconOpenAlt,
        item.iconOpenSize ?? item.iconSize,
      );
      if (opened) {
        return (
          <>
            <span className="inline group-data-[state=open]:hidden">{closed}</span>
            <span className="hidden group-data-[state=open]:inline">{opened}</span>
          </>
        );
      }
      return closed;
    };

    if (props.type === "multiple") {
      return (
        <Accordion
          ref={ref}
          type="multiple"
          className={cn(containerClassName, containerClass)}
          defaultValue={props.defaultValue}
          onValueChange={props.onValueChange}
        >
          {items.map((item) => (
            <AccordionItem
              key={String(item.id)}
              value={String(item.id)}
              className={cn(itemClassName, props.itemBorderClassName, item.className)}
              disabled={item.disabled}
            >
              <AccordionTrigger
                icon={props.triggerIcon}
                iconPosition={props.triggerIconPosition}
                className={cn(
                  "hover:no-underline",
                  props.triggerBgClassName,
                  triggerClassName,
                  // open-state text color helper
                  props.triggerTextOpenClassName &&
                    `data-[state=open]:${props.triggerTextOpenClassName}`,
                  item.triggerClassName,
                )}
                data-testid={`accordion-trigger-${item.id}-btn`}
              >
                <div className="flex items-center gap-2">
                  {renderIcons(item)}
                  <span className={cn("text-left", titleClassName, item.titleClassName)}>
                    {item.title}
                  </span>
                </div>
                {item.rightText && (
                  <span
                    className={cn(
                      "text-muted-foreground text-sm",
                      rightTextClassName,
                      item.rightTextClassName,
                    )}
                  >
                    {item.rightText}
                  </span>
                )}
              </AccordionTrigger>
              <AccordionContent
                className={cn(props.contentBgClassName, contentClassName, item.contentClassName)}
                data-testid={`accordion-${item?.id}-content`}
              >
                {item.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      );
    }

    // single (default)
    const singleProps = props as CommonProps & SingleAccordionProps;
    return (
      <Accordion
        ref={ref}
        type="single"
        collapsible={singleProps.collapsible ?? true}
        className={cn(containerClassName, containerClass)}
        value={singleProps.value}
        defaultValue={singleProps.defaultValue}
        onValueChange={singleProps.onValueChange}
      >
        {items.map((item) => (
          <AccordionItem
            key={String(item.id)}
            value={String(item.id)}
            className={cn(itemClassName, singleProps.itemBorderClassName, item.className)}
            disabled={item.disabled}
          >
            <AccordionTrigger
              icon={singleProps.triggerIcon}
              iconPosition={singleProps.triggerIconPosition}
              className={cn(
                "hover:no-underline",
                singleProps.triggerBgClassName,
                triggerClassName,
                singleProps.triggerTextOpenClassName &&
                  `data-[state=open]:${singleProps.triggerTextOpenClassName}`,
                item.triggerClassName,
              )}
              data-testid={`accordion-trigger-${item.id}-btn`}
            >
              <div className="flex items-center gap-2">
                {renderIcons(item)}
                <span className={cn("text-left", titleClassName, item.titleClassName)}>
                  {item.title}
                </span>
              </div>
              {item.rightText && (
                <span
                  className={cn(
                    "text-muted-foreground text-sm",
                    rightTextClassName,
                    item.rightTextClassName,
                  )}
                >
                  {item.rightText}
                </span>
              )}
            </AccordionTrigger>
            <AccordionContent
              className={cn(
                singleProps.contentBgClassName,
                contentClassName,
                item.contentClassName,
              )}
              data-testid={`accordion-${item?.id}-content`}
            >
              {item.content}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  },
);

AccordionField.displayName = "AccordionField";

export { AccordionField };
